import audioData from '../assets/audio/index';

class AudioManager {
  private audioContext: AudioContext | null = null;
  private audioBuffers: Map<string, AudioBuffer> = new Map();
  private isInitialized = false;
  private isIOS = false;
  private unlockAudioContext: (() => void) | null = null;

  constructor() {
    this.detectIOS();
    this.initializeAudioContext();
  }

  private detectIOS(): void {
    this.isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  private async initializeAudioContext(): Promise<void> {
    try {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      this.audioContext = new AudioContextClass({
        latencyHint: 'interactive',
        sampleRate: 44100,
      });

      if (this.isIOS && this.audioContext.state === 'suspended') {
        this.unlockAudioContext = async () => {
          if (this.audioContext && this.audioContext.state === 'suspended') {
            try {
              await this.audioContext.resume();
            } catch (error) {
              console.warn('Failed to unlock iOS audio context:', error);
            }
          }
        };

        const unlockEvents: Array<keyof DocumentEventMap> = [
          'touchstart',
          'touchend',
          'click',
        ];
        const unlockHandler = () => {
          this.unlockAudioContext?.();
          unlockEvents.forEach((event) => {
            document.removeEventListener(event, unlockHandler);
          });
        };
        unlockEvents.forEach((event) => {
          document.addEventListener(event, unlockHandler, { once: true });
        });
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize AudioContext:', error);
    }
  }

  private async loadAudioBuffer(url: string, key: string): Promise<void> {
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();

      if (this.audioContext) {
        const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        this.audioBuffers.set(key, audioBuffer);
      }
    } catch (error) {
      console.error(`Failed to load audio buffer for ${key}:`, error);
    }
  }

  public async preloadAllAudio(): Promise<void> {
    if (!this.isInitialized || !this.audioContext) {
      await this.initializeAudioContext();
    }

    const loadPromises: Promise<void>[] = [];

    Object.entries(audioData).forEach(([key, url]) => {
      if (!this.audioBuffers.has(key)) {
        loadPromises.push(this.loadAudioBuffer(url as string, key));
      }
    });

    try {
      await Promise.all(loadPromises);
    } catch (error) {
      console.error('Failed to preload audio files:', error);
    }
  }

  public async playAudio(key: string, volume: number = 1.0): Promise<void> {
    if (!this.isInitialized || !this.audioContext) {
      console.warn('AudioManager not initialized');
      return;
    }

    if (this.isIOS && this.audioContext.state === 'suspended') {
      await this.unlockAudioContext?.();
    }

    const audioBuffer = this.audioBuffers.get(key);
    if (!audioBuffer) {
      console.warn(`Audio buffer not found for key: ${key}`);
      return;
    }

    try {
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;

      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = volume;

      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      source.start(0);
    } catch (error) {
      console.error(`Failed to play audio ${key}:`, error);
    }
  }

  public stopAllAudio(): void {
    if (this.audioContext) {
      // Short tones; rely on natural completion. Extend if explicit stop-all is needed.
    }
  }
}

const audioManager = new AudioManager();

export default audioManager;
